import React from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';
import type { BaseMappingProps } from './index';
import type { SettingspageTranslations } from '@/hooks/useSettingspageTranslations';

export interface MappingSection {
  title: string;
  description?: string;
  fields: MappingField[];
  isAutomatic?: boolean;
}

export interface MappingField {
  key: string;
  label: string;
  description?: string;
  placeholder?: string;
  type: 'attribute' | 'price_type' | 'boolean' | 'text';
  required?: boolean;
  filterType?: string[]; // For attributes: filter by data_type
  allowDefault?: boolean; // Show "Use default" option
  allowNone?: boolean; // Show "No mapping" option
}

interface BaseConnectorMappingProps extends BaseMappingProps {
  sections: MappingSection[];
  mappingPath?: string; // e.g., "settings.mapping"
  i18n: SettingspageTranslations;
}

export function BaseConnectorMapping({
  form,
  attributes,
  priceTypes,
  selectedPlatform: _selectedPlatform,
  sections,
  mappingPath = 'settings.mapping',
  i18n,
}: BaseConnectorMappingProps) {
  const getFieldValue = (fieldKey: string) => {
    return form.watch(`${mappingPath}.${fieldKey}`) || undefined;
  };

  const setFieldValue = (fieldKey: string, value: string) => {
    form.setValue(`${mappingPath}.${fieldKey}`, value);
  };

  // Define which fields actually have defaults in KernLogic core
  const CORE_FIELD_DEFAULTS = {
    body_html_attribute: 'description',
    vendor_attribute: 'brand',
    'variants.barcode_attribute': 'barcode',
    tags_attribute: 'tags',
    status_attribute: 'is_active',
    // Note: inventory fields (quantity, management, policy) are NOT core fields
    // They need to be mapped to custom attributes - no defaults available
  };

  const getDefaultLabel = (fieldKey: string) => {
    const coreField = CORE_FIELD_DEFAULTS[fieldKey as keyof typeof CORE_FIELD_DEFAULTS];
    return coreField
      ? `${i18n.api.modal.configuration.baseMapping.common.useDefault} (product.${coreField})`
      : i18n.api.modal.configuration.baseMapping.common.useDefault;
  };

  const getTransformationHint = (dataType: string, fieldType?: string) => {
    // Show how different attribute types will be transformed
    switch (dataType) {
      case 'select':
        return i18n.api.modal.configuration.baseMapping.transformationHints.selectedValueOnly;
      case 'measurement':
        return fieldType === 'weight'
          ? i18n.api.modal.configuration.baseMapping.transformationHints.valueUnitSeparately
          : i18n.api.modal.configuration.baseMapping.transformationHints.valueUnitText;
      case 'rich_text':
        return fieldType === 'description'
          ? i18n.api.modal.configuration.baseMapping.transformationHints.htmlPreserved
          : i18n.api.modal.configuration.baseMapping.transformationHints.plainText;
      case 'boolean':
        return i18n.api.modal.configuration.baseMapping.transformationHints.trueFalseText;
      case 'url':
        return i18n.api.modal.configuration.baseMapping.transformationHints.urlText;
      case 'number':
        return i18n.api.modal.configuration.baseMapping.transformationHints.numberAsText;
      case 'date':
        return i18n.api.modal.configuration.baseMapping.transformationHints.formattedDate;
      default:
        return '';
    }
  };

  const renderAttributeSelect = (field: MappingField) => {
    // Show ALL attributes - remove restrictive filtering
    // Users can map any attribute type, and we'll transform it appropriately
    const allAttributes = attributes;

    // Only show "Use default" if this field actually has a core field default
    const hasActualDefault =
      field.allowDefault && Object.prototype.hasOwnProperty.call(CORE_FIELD_DEFAULTS, field.key);

    // Determine field context for transformation hints
    const fieldContext = field.key.includes('weight')
      ? 'weight'
      : field.key.includes('description') || field.key.includes('body_html')
        ? 'description'
        : 'general';

    const placeholder =
      field.placeholder ||
      i18n.api.modal.configuration.baseMapping.placeholders.selectMapping.replace(
        '{fieldName}',
        field.label.toLowerCase()
      );

    return (
      <Select
        value={getFieldValue(field.key)}
        onValueChange={value => setFieldValue(field.key, value)}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {hasActualDefault && (
            <SelectItem value="__default__">{getDefaultLabel(field.key)}</SelectItem>
          )}
          {field.allowNone && (
            <SelectItem value="__none__">
              {i18n.api.modal.configuration.baseMapping.placeholders.noMappingFor.replace(
                '{fieldName}',
                field.label.toLowerCase()
              )}
            </SelectItem>
          )}
          {allAttributes.map(attr => {
            const transformHint = getTransformationHint(attr.data_type, fieldContext);
            return (
              <SelectItem key={attr.id} value={attr.code}>
                <div className="flex flex-col">
                  <span>
                    {attr.label} ({attr.code})
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {attr.data_type}
                    {transformHint ? ` ${transformHint}` : ''}
                  </span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  };

  const renderPriceTypeSelect = (field: MappingField) => {
    const placeholder =
      field.placeholder ||
      i18n.api.modal.configuration.baseMapping.placeholders.selectMapping.replace(
        '{fieldName}',
        field.label.toLowerCase()
      );

    return (
      <Select
        value={getFieldValue(field.key)}
        onValueChange={value => setFieldValue(field.key, value)}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {field.allowNone && (
            <SelectItem value="__none__">
              {i18n.api.modal.configuration.baseMapping.placeholders.noMappingFor.replace(
                '{fieldName}',
                field.label.toLowerCase()
              )}
            </SelectItem>
          )}
          {priceTypes.map(priceType => (
            <SelectItem key={priceType.id} value={priceType.code}>
              {priceType.label} ({priceType.code})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  };

  const renderBooleanSelect = (field: MappingField) => {
    const placeholder =
      field.placeholder ||
      i18n.api.modal.configuration.baseMapping.placeholders.selectMapping.replace(
        '{fieldName}',
        field.label.toLowerCase()
      );

    return (
      <Select
        value={getFieldValue(field.key)}
        onValueChange={value => setFieldValue(field.key, value)}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {field.allowDefault && (
            <SelectItem value="__default__">
              {i18n.api.modal.configuration.baseMapping.common.useDefault}
            </SelectItem>
          )}
          <SelectItem value="true">
            {i18n.api.modal.configuration.baseMapping.common.yesTrue}
          </SelectItem>
          <SelectItem value="false">
            {i18n.api.modal.configuration.baseMapping.common.noFalse}
          </SelectItem>
          {field.allowNone && (
            <SelectItem value="__none__">
              {i18n.api.modal.configuration.baseMapping.common.noMapping}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    );
  };

  const renderField = (field: MappingField) => {
    let input: React.ReactNode;

    switch (field.type) {
      case 'attribute':
        input = renderAttributeSelect(field);
        break;
      case 'price_type':
        input = renderPriceTypeSelect(field);
        break;
      case 'boolean':
        input = renderBooleanSelect(field);
        break;
      case 'text':
        input = (
          <input
            type="text"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={getFieldValue(field.key)}
            onChange={e => setFieldValue(field.key, e.target.value)}
            placeholder={field.placeholder}
          />
        );
        break;
      default:
        input = null;
    }

    return (
      <div key={field.key} className="space-y-1">
        <Label className="text-xs">
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </Label>
        {field.description && <p className="text-xs text-muted-foreground">{field.description}</p>}
        {input}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {sections.map((section, index) => (
        <Card key={index}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">{section.title}</CardTitle>
              {section.isAutomatic && (
                <Badge variant="outline" className="border-green-600 text-green-600">
                  <CheckCircle className="mr-1 size-3" />
                  {i18n.api.modal.configuration.baseMapping.common.automatic}
                </Badge>
              )}
            </div>
            {section.description && (
              <p className="text-xs text-muted-foreground">{section.description}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {section.isAutomatic ? (
              <div className="rounded-lg bg-green-50 p-3">
                <div className="text-xs text-green-700">
                  {i18n.api.systemMessages.autoMappingDescription}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {section.fields.map(renderField)}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
